// @steered AudibleSecurityContext 2026-05-04 2026-07-08
/**
 * Marketplace Domain Mapping Utility
 * 
 * Provides domain mappings for Audible marketplaces and URL construction
 * for browser validation after record import.
 * 
 * Requirements: 5.2, 5.3
 */

/**
 * Marketplace to Audible domain mapping
 * Maps marketplace codes to their corresponding Audible domains
 */
export const MARKETPLACE_DOMAINS: Record<string, string> = {
  'US': 'audible.com',
  'us': 'audible.com',
  'UK': 'audible.co.uk',
  'uk': 'audible.co.uk',
  'DE': 'audible.de',
  'de': 'audible.de',
  'FR': 'audible.fr',
  'fr': 'audible.fr',
  'IT': 'audible.it',
  'it': 'audible.it',
  'ES': 'audible.es',
  'es': 'audible.es',
  'JP': 'audible.co.jp',
  'jp': 'audible.co.jp',
  'AU': 'audible.com.au',
  'au': 'audible.com.au',
  'CA': 'audible.ca',
  'ca': 'audible.ca',
  'IN': 'audible.in',
  'in': 'audible.in',
  'amazon-us': 'amazon.com',
  'amazon-uk': 'amazon.co.uk',
  'amazon-ca': 'amazon.ca',
  'amazon-au': 'amazon.com.au',
  'amazon-de': 'amazon.de',
  'amazon-fr': 'amazon.fr',
  'amazon-it': 'amazon.it',
  'amazon-es': 'amazon.es',
  'amazon-in': 'amazon.in',
  'amazon-jp': 'amazon.co.jp',
};

/**
 * Supported page types for validation URLs
 */
export type PageType = 'AudiblePDP' | 'AudibleListView' | 'AmazonDP' | 'pdp' | 'list-view' | 'amazon-dp';

/**
 * Marketplace to region-specific G2S2 label mapping.
 * NA (IAD): AudibleCBBProdNA, EU (DUB): AudibleCBBProdEU, FE (PDX): AudibleCBBProdFE
 */
const MARKETPLACE_G2S2_LABEL: Record<string, string> = {
  'US': 'AudibleCBBProdNA', 'CA': 'AudibleCBBProdNA', 'BR': 'AudibleCBBProdNA',
  'UK': 'AudibleCBBProdEU', 'GB': 'AudibleCBBProdEU',
  'DE': 'AudibleCBBProdEU', 'FR': 'AudibleCBBProdEU',
  'IT': 'AudibleCBBProdEU', 'ES': 'AudibleCBBProdEU',
  'JP': 'AudibleCBBProdFE', 'AU': 'AudibleCBBProdFE', 'IN': 'AudibleCBBProdFE',
};

/**
 * Gets the G2S2 parent label for a marketplace.
 * Defaults to AudibleCBBProdNA if marketplace is unknown.
 */
export function getG2s2Label(marketplace: string): string {
  const upper = marketplace.toUpperCase();
  // Strip amazon- prefix if present (e.g., AMAZON-US -> US)
  const stripped = upper.startsWith('AMAZON-') ? upper.replace('AMAZON-', '') : upper;
  return MARKETPLACE_G2S2_LABEL[stripped] || 'AudibleCBBProdNA';
}

/**
 * Configuration for building validation URLs
 */
export interface ValidationUrlConfig {
  /** Page type - either PDP, ListView, or AmazonDP */
  pageType: PageType;
  /** Marketplace code (e.g., 'US', 'UK', 'DE') */
  marketplace: string;
  /** Label name returned from G2S2HelperFunction */
  labelName: string;
  /** Product path for Audible PDP pages (e.g., "Earth-Audiobook/B00821B3D2") */
  productPath?: string;
  /** ASIN for Amazon DP pages */
  asin?: string;
  /** Keywords for ListView search pages */
  keywords?: string;
  /** Weblab with treatment (e.g., "G2S2_1355070:T1") for weblabOverride parameter */
  weblab?: string;
}

/**
 * Gets the Audible domain for a given marketplace
 * @param marketplace - Marketplace code (e.g., 'US', 'UK')
 * @returns The Audible domain for the marketplace, defaults to 'audible.com'
 */
export function getMarketplaceDomain(marketplace: string): string {
  return MARKETPLACE_DOMAINS[marketplace] || MARKETPLACE_DOMAINS[marketplace.toLowerCase()] || MARKETPLACE_DOMAINS[marketplace.toUpperCase()] || 'audible.com';
}

/**
 * Gets the Amazon domain for a given marketplace.
 * Handles both prefixed (amazon-us, AMAZON-US) and plain (US, us) marketplace codes.
 * @param marketplace - Marketplace code (e.g., 'AMAZON-US', 'amazon-us', 'US')
 * @returns The Amazon domain for the marketplace, defaults to 'amazon.com'
 */
export function getAmazonDomain(marketplace: string): string {
  const lower = marketplace.toLowerCase();
  // If already prefixed with amazon-, look it up directly
  if (lower.startsWith('amazon-')) {
    return MARKETPLACE_DOMAINS[lower] || 'amazon.com';
  }
  // Otherwise, add the prefix
  return MARKETPLACE_DOMAINS[`amazon-${lower}`] || 'amazon.com';
}

/**
 * Normalizes page type to standard format
 * @param pageType - Page type from form (could be 'pdp', 'list-view', 'AudiblePDP', 'AudibleListView')
 * @returns Normalized page type
 */
function normalizePageType(pageType: PageType): 'AudiblePDP' | 'AudibleListView' | 'AmazonDP' {
  if (pageType === 'pdp' || pageType === 'AudiblePDP') {
    return 'AudiblePDP';
  }
  if (pageType === 'amazon-dp' || pageType === 'AmazonDP') {
    return 'AmazonDP';
  }
  return 'AudibleListView';
}

/**
 * Builds a validation URL for browser preview after record import
 * 
 * Requirements:
 * - 5.2: Construct validation URL based on page type
 * - 5.3: Use marketplace-appropriate domain
 * 
 * @param config - Configuration object with page type, marketplace, label name, and optional product/keyword info
 * @returns The constructed validation URL
 * 
 * @example
 * // PDP URL
 * buildValidationUrl({
 *   pageType: 'AudiblePDP',
 *   marketplace: 'US',
 *   labelName: 'my-label',
 *   productPath: 'Earth-Audiobook/B00821B3D2',
 *   weblab: 'G2S2_1355070:T1'
 * })
 * // Returns: https://www.audible.com/pd/Earth-Audiobook/B00821B3D2?g2s2StageVersionOverride={AudibleCBBProdNA:my-label}&weblabOverride=G2S2_1355070:T1
 * 
 * @example
 * // ListView URL
 * buildValidationUrl({
 *   pageType: 'AudibleListView',
 *   marketplace: 'UK',
 *   labelName: 'my-label',
 *   keywords: 'harry potter',
 *   weblab: 'G2S2_1355070:T1'
 * })
 * // Returns: https://www.audible.co.uk/search?keywords=harry%20potter&k=harry%20potter&g2s2StageVersionOverride={AudibleCBBProdEU:my-label}&weblabOverride=G2S2_1355070:T1
 */
export function buildValidationUrl(config: ValidationUrlConfig): string {
  const domain = getMarketplaceDomain(config.marketplace);
  const normalizedPageType = normalizePageType(config.pageType);
  
  if (normalizedPageType === 'AmazonDP') {
    // Amazon DP URL format: https://www.{domain}/dp/<ASIN>?g2s2StageVersionOverride={<region-label>:<label-name>}
    // Note: Amazon does NOT support weblabOverride in the URL
    const amazonDomain = getAmazonDomain(config.marketplace);
    const asin = config.asin || config.productPath || '';
    const g2s2Label = getG2s2Label(config.marketplace);
    const stageOverride = `g2s2StageVersionOverride={${g2s2Label}:${config.labelName}}`;
    return `https://www.${amazonDomain}/dp/${asin}?${stageOverride}`;
  }
  
  const g2s2Label = getG2s2Label(config.marketplace);
  const stageOverride = `g2s2StageVersionOverride={${g2s2Label}:${config.labelName}}`;
  const weblabOverride = config.weblab ? `&weblabOverride=${config.weblab}` : '';

  if (normalizedPageType === 'AudiblePDP') {
    // PDP URL format: https://www.{domain}/pd/{product-path}?g2s2StageVersionOverride={<region-label>:<label-name>}&weblabOverride=<weblab:treatment>
    const productPath = config.productPath || '';
    return `https://www.${domain}/pd/${productPath}?${stageOverride}${weblabOverride}`;
  } else {
    // ListView URL format: https://www.{domain}/search?keywords={keywords}&k={keywords}&g2s2StageVersionOverride={<region-label>:<label-name>}&weblabOverride=<weblab:treatment>
    const encodedKeywords = encodeURIComponent(config.keywords || '');
    return `https://www.${domain}/search?keywords=${encodedKeywords}&k=${encodedKeywords}&${stageOverride}${weblabOverride}`;
  }
}

/**
 * Extracts ASIN from a product path
 * @param productPath - Product path like "Earth-Audiobook/B00821B3D2"
 * @returns The ASIN if found, or undefined
 */
export function extractAsinFromProductPath(productPath: string): string | undefined {
  // ASIN is typically a 10-character alphanumeric code starting with B
  const asinMatch = productPath.match(/\b(B[0-9A-Z]{9})\b/);
  return asinMatch ? asinMatch[1] : undefined;
}

/**
 * Builds a product path from title and ASIN
 * @param title - Product title (will be slugified)
 * @param asin - Product ASIN
 * @returns Product path in format "Title-Slug/ASIN"
 */
export function buildProductPath(title: string, asin: string): string {
  // Slugify the title: replace spaces with hyphens, remove special chars
  const slug = title
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
  
  return `${slug}/${asin}`;
}
